import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, Shield, TrendingUp, Box, MapPin, 
  BarChart3, FileText, CheckCircle, Lock,
  Home, Bed, Bath, Ruler, Award,
  Heart, Sparkles, ArrowRight, Calculator, Gavel,
  Pencil, Map as MapIcon
} from 'lucide-react';
import { propertiesAPI } from '../services/api';
import contentService from '../services/contentService';
import SEO, { buildOrganizationJsonLd } from '../components/SEO';
import HeroIllustration from '../components/HeroIllustration';
import AISearchBar from '../components/AISearchBar';
import FeaturedAgentsCarousel from '../components/FeaturedAgentsCarousel';
import LocationCombobox from '../components/LocationCombobox';
import FeatureShowcaseGrid from '../components/FeatureShowcaseGrid';
import FeaturesHeroCard from '../components/FeaturesHeroCard';
import RecentlyViewedRail from '../components/RecentlyViewedRail';
import HomePropertyRail from '../components/HomePropertyRail';
import RecentSalesRail from '../components/RecentSalesRail';
import ShareShortlistButton from '../components/ShareShortlistButton';
import PropertyTypeChips from '../components/PropertyTypeChips';
import PostalSearchBox from '../components/PostalSearchBox';
import { useMarket } from '../hooks/useMarket';
import { useAuth } from '../context/AuthContext';
import { savedAPI } from '../services/api';
import { initSaved, subscribe as subscribeSaved } from '../utils/savedStore';
import { readRecentSearches, recordSearch, relevanceScore } from '../utils/recentSearches';
import { groupedTypesForMode } from '../utils/propertyTypes';

const HomePageProfessional = ({ countryOverride } = {}) => {
  const navigate = useNavigate();
  // countryOverride lets a route (e.g. `/uk`) force the market so the
  // same component renders in UK-flavour without asking the visitor to
  // click the market switcher.
  const market = useMarket(countryOverride);
  const isUK = market.isUK;
  const isUS = market.isUS;
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyType, setPropertyType] = useState('all');
  const [listingType, setListingType] = useState('all');
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [rails, setRails] = useState({ recent: [], goodValue: [], newHomes: [], rentals: [] });
  const [savedHomes, setSavedHomes] = useState([]);
  const { isAuthenticated } = useAuth();
  const [content, setContent] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing/public`)
      .then((r) => r.json())
      .then((d) => setPricing(d?.subscription_plans || {}))
      .catch(() => {});
  }, []);

  const oneOffPrice = pricing?.one_off_seller?.price;
  const fsboLine = (oneOffPrice != null && oneOffPrice > 0)
    ? `List free or one-time ${market.currency_symbol}${oneOffPrice}`
    : 'List free — pay only when ready';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Curated rail pools — one big sales pool + a smaller rentals pool.
        // We partition them into 4 rails client-side with a shared `usedIds`
        // set so no property is ever repeated across rails.
        const [contentData, salesPool, rentalsPool, areasRes] = await Promise.all([
          contentService.getContent(),
          propertiesAPI.getAll({ type: 'sale', limit: 40, country: market.name || undefined }),
          propertiesAPI.getAll({ type: 'rent', limit: 10, country: market.name || undefined }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/areas?type=sale&country=${encodeURIComponent(market.name || 'Ireland')}`)
            .then((r) => r.json())
            .catch(() => ({ counties: [] })),
        ]);

        const salesArr = Array.isArray(salesPool) ? [...salesPool] : [];
        const rentalsArr = Array.isArray(rentalsPool) ? [...rentalsPool] : [];

        // NOTE: previously we topped up thin UK / US inventory with a
        // country-less fallback fetch. That fallback defaulted to
        // Ireland on the backend, which leaked Irish listings onto the
        // UK / US homepage rails (the "market data bleed" bug the user
        // has reported repeatedly). Better to show fewer real matches
        // than to mix in the wrong market.

        const usedIds = new Set();
        const take = (arr, n) => {
          const out = [];
          for (const p of arr) {
            const pid = p._id || p.id;
            if (!pid || usedIds.has(pid)) continue;
            usedIds.add(pid);
            out.push(p);
            if (out.length >= n) break;
          }
          return out;
        };

        const priceThreshold = market.isUK ? 350000 : market.isUS ? 500000 : 400000;

        // Personalised sort for "Fresh on the market" — combines recency
        // (newer = better) with a soft boost when the property matches
        // one of the visitor's recent search locations. Falls back to
        // pure recency when there's no local history yet.
        const recentSearches = readRecentSearches();
        const now = Date.now();
        const rankedForYou = [...salesArr].sort((a, b) => {
          const relA = relevanceScore(a, recentSearches);
          const relB = relevanceScore(b, recentSearches);
          const ageA = (now - new Date(a.createdAt || 0).getTime()) / (1000 * 60 * 60 * 24);
          const ageB = (now - new Date(b.createdAt || 0).getTime()) / (1000 * 60 * 60 * 24);
          // Recency score: fresh (0 days) ~10, half-life ~14 days.
          const freshA = 10 * Math.exp(-ageA / 14);
          const freshB = 10 * Math.exp(-ageB / 14);
          return (relB + freshB) - (relA + freshA);
        });
        const recent = take(rankedForYou, 8);
        const newHomes = take(
          salesArr.filter((p) => p.isNewHome === true || p.propertyType === 'new-home'),
          8,
        );
        const goodValue = take(
          [...salesArr]
            .filter((p) => (p.price || 0) > 0 && (p.price || 0) <= priceThreshold)
            .sort((a, b) => (a.price || 0) - (b.price || 0)),
          8,
        );
        const rentals = take(rentalsArr, 8);

        setContent(contentData);
        setRails({ recent, goodValue, newHomes, rentals });
        // Keep the legacy `featuredProperties` populated in case any child
        // section still references it — recent rail doubles as featured.
        setFeaturedProperties(recent.slice(0, 3));
        setAreas(areasRes.counties || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setContent(contentService.getFallbackContent());
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [market.code]);

  // Saved homes — only fetched when the visitor is signed in. Kept in a
  // separate effect so the anonymous rails aren't blocked while it loads.
  // Also subscribes to the shared `savedStore` so hearting a property from
  // any other rail card instantly refreshes this rail without a reload.
  useEffect(() => {
    // Sync the shared store with auth state.
    initSaved(isAuthenticated);

    if (!isAuthenticated) {
      setSavedHomes([]);
      return undefined;
    }

    let cancelled = false;
    const load = () => {
      savedAPI
        .getAll()
        .then((res) => {
          if (cancelled) return;
          const arr = Array.isArray(res) ? res : (res?.properties || []);
          setSavedHomes(arr.slice(0, 12));
        })
        .catch(() => {
          if (!cancelled) setSavedHomes([]);
        });
    };
    load();
    // Re-fetch whenever any card in the tree toggles a save.
    const unsub = subscribeSaved(() => load());
    return () => {
      cancelled = true;
      unsub();
    };
  }, [isAuthenticated]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (listingType !== 'all') params.append('type', listingType);
    if (searchQuery) params.append('location', searchQuery);
    if (propertyType !== 'all') params.append('propertyType', propertyType);
    // Remember this search so we can personalise the "Fresh on the market"
    // rail next time the visitor lands on the homepage.
    recordSearch({
      location: searchQuery,
      listingType: listingType !== 'all' ? listingType : null,
      propertyType: propertyType !== 'all' ? propertyType : null,
    });
    navigate(market.withPrefix(`/listings?${params.toString()}`));
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="lovinahome — so close to home"
        description={content?.hero?.description || (isUK
          ? 'Friendly property search for the UK. Real listings, real prices, real help.'
          : 'Friendly property search for Ireland. Real listings, real prices, real help.')}
        path="/"
        jsonLd={buildOrganizationJsonLd()}
      />
      {/* Hero Section — full-bleed image, navbar floats over the top */}
      <section className="relative overflow-hidden -mt-16 min-h-[760px] flex items-center">
        {/* CSS-only entrance animations + slow Ken-Burns zoom on the bg image */}
        <style>{`
          @keyframes lhFadeUp {
            from { opacity: 0; transform: translate3d(0, 14px, 0); }
            to   { opacity: 1; transform: translate3d(0, 0, 0); }
          }
          @keyframes lhKenBurns {
            from { transform: scale(1.0); }
            to   { transform: scale(1.08); }
          }
          .lh-hero-anim {
            opacity: 0;
            animation: lhFadeUp 720ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
            will-change: transform, opacity;
          }
          .lh-hero-bg {
            animation: lhKenBurns 22s ease-in-out infinite alternate;
            will-change: transform;
          }
          @media (prefers-reduced-motion: reduce) {
            .lh-hero-anim { animation: none; opacity: 1; }
            .lh-hero-bg { animation: none; }
          }
        `}</style>

        {/* Full-bleed background image — warm, residential, golden-hour Irish home */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center lh-hero-bg"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80')",
          }}
          aria-hidden="true"
        />
        {/* Dark gradient + brand-purple wash for legibility */}
        <div
          className="absolute inset-0 z-[1] bg-gradient-to-br from-[#1a0f2e]/85 via-[#3d1f6b]/65 to-[#1a0f2e]/45"
          aria-hidden="true"
        />
        <div
          className="absolute inset-y-0 left-0 w-2/3 z-[1] bg-gradient-to-r from-[#5b35a8]/60 via-[#5b35a8]/20 to-transparent"
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 sm:pt-36 sm:pb-28 w-full">
          <div className="max-w-2xl">
            <div
              className="lh-hero-anim mb-5 inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-md border border-white/30 text-[11px] font-semibold text-white tracking-wider uppercase rounded-full shadow-sm"
              style={{ animationDelay: '40ms' }}
            >
              <Shield size={13} />
              {isUK
                ? (content.hero.badge_text || '').replace(/IRISH/gi, 'UK').replace(/Ireland/gi, 'UK') || 'VERIFIED UK LISTINGS'
                : content.hero.badge_text}
            </div>

            <h1
              className="lh-hero-anim text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-6 text-white"
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                animationDelay: '160ms',
              }}
            >
              <span style={{ fontWeight: 500 }}>So</span>{' '}
              <span style={{ fontWeight: 800, color: '#FFB07C' }}>close</span>{' '}
              <span style={{ fontWeight: 500 }}>to home.</span>
            </h1>

            <p
              className="lh-hero-anim text-lg sm:text-xl text-white/95 leading-relaxed mb-9 max-w-xl"
              style={{
                fontWeight: 400,
                textShadow: '0 1px 12px rgba(0,0,0,0.4)',
                animationDelay: '280ms',
              }}
            >
              {content.hero.description}
            </p>

            {/* Trust pills — frosted glass on dark image */}
            <div
              className="lh-hero-anim flex flex-wrap gap-3 mb-10"
              style={{ animationDelay: '380ms' }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30">
                <span className="text-xl font-bold text-white">100%</span>
                <span className="text-xs text-white/85">real listings</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30">
                <span className="text-xl font-bold text-emerald-300">Save {market.currency_symbol}1000s</span>
                <span className="text-xs text-white/85">with our smart buyer AI tools</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30">
                <span className="text-xl font-bold text-amber-300">A.I.</span>
                <span className="text-xs text-white/85">in your corner</span>
              </div>
            </div>

            {/* AI natural-language search — buyers can type a full
                sentence ("3 bed house under 300k in Wicklow") and
                we'll parse it via Claude and route them to the right
                page with filters pre-applied. Sits ABOVE the
                structured pill bar so both styles work. */}
            <div className="lh-hero-anim mb-3" style={{ animationDelay: '440ms' }}>
              <AISearchBar testId="hero-ai-search" />
            </div>

            {/* Search Container — Airbnb-style pill bar with labelled sections */}
            <div
              className="lh-hero-anim bg-white border border-white/40 shadow-2xl shadow-black/30 rounded-2xl p-2 sm:p-2"
              style={{ animationDelay: '480ms' }}
            >
              {/* Desktop / tablet — single pill row with internal dividers */}
              <div className="hidden md:flex items-stretch">
                {/* Location */}
                <div className="flex-[1.6] px-4 py-2 hover:bg-gray-50 rounded-l-xl transition-colors group">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 group-hover:text-[#7042cc] mb-0.5">
                    Where
                  </div>
                  <LocationCombobox
                    counties={areas}
                    value={searchQuery}
                    onChange={setSearchQuery}
                  />
                </div>
                {/* Divider */}
                <div className="self-center w-px h-10 bg-gray-200" aria-hidden />
                {/* Listing type */}
                <div className="flex-[0.9] px-4 py-2 hover:bg-gray-50 transition-colors group">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 group-hover:text-[#7042cc] mb-0.5">
                    I want to
                  </div>
                  <Select value={listingType} onValueChange={setListingType}>
                    <SelectTrigger
                      className="h-7 px-0 border-0 shadow-none bg-transparent focus:ring-0 focus:ring-offset-0 text-sm font-medium text-gray-900 [&>svg]:text-gray-400"
                      data-testid="hero-listing-type-trigger"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Buy or rent</SelectItem>
                      <SelectItem value="sale">Buy</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="share">Share</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Divider */}
                <div className="self-center w-px h-10 bg-gray-200" aria-hidden />
                {/* Property type */}
                <div className="flex-[1.4] px-4 py-2 hover:bg-gray-50 transition-colors group">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 group-hover:text-[#7042cc] mb-0.5 whitespace-nowrap">
                    Property
                  </div>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger
                      className="h-7 px-0 border-0 shadow-none bg-transparent focus:ring-0 focus:ring-offset-0 text-sm font-medium text-gray-900 [&>svg]:text-gray-400"
                      data-testid="hero-property-type-trigger"
                    >
                      <SelectValue placeholder="Any type" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                      <SelectItem value="all" data-testid="hero-property-type-option-all">
                        Any type
                      </SelectItem>
                      {groupedTypesForMode(listingType === 'all' ? 'sale' : listingType).map((grp) => (
                        <div key={grp.id}>
                          <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
                            {grp.label}
                          </div>
                          {grp.items.map((t) => (
                            <SelectItem
                              key={t.slug}
                              value={t.slug}
                              data-testid={`hero-property-type-option-${t.slug}`}
                            >
                              {t.label}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Search button — purple CTA tucked to the right */}
                <div className="flex items-center pl-2 pr-1">
                  <Button
                    onClick={handleSearch}
                    className="h-12 px-6 bg-[#7042cc] hover:bg-[#5b35a8] text-white rounded-xl font-medium shadow-md shadow-[#7042cc]/25 whitespace-nowrap"
                    data-testid="hero-search-button"
                  >
                    <Search size={18} className="mr-2" />
                    {content.hero.cta_button}
                  </Button>
                </div>
              </div>

              {/* Mobile — stacked vertical */}
              <div className="md:hidden grid grid-cols-1 gap-2 p-1">                <LocationCombobox
                  counties={areas}
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
                <Select value={listingType} onValueChange={setListingType}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 bg-gray-50/60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Buy or rent</SelectItem>
                    <SelectItem value="sale">Buy</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="share">Share</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 bg-gray-50/60">
                    <SelectValue placeholder="Any property type" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[360px]">
                    <SelectItem value="all">Any property type</SelectItem>
                    {groupedTypesForMode(listingType === 'all' ? 'sale' : listingType).map((grp) => (
                      <div key={grp.id}>
                        <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
                          {grp.label}
                        </div>
                        {grp.items.map((t) => (
                          <SelectItem key={t.slug} value={t.slug}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleSearch}
                  className="w-full h-12 bg-[#7042cc] hover:bg-[#5b35a8] text-white rounded-xl font-medium shadow-md shadow-[#7042cc]/20"
                >
                  <Search size={18} className="mr-2" />
                  {content.hero.cta_button}
                </Button>
              </div>
            </div>

            {/* Quick-pick property type chips — one tap into SEO landing pages */}
            <PropertyTypeChips />

            {/* Search by map — prominent secondary CTA below the hero
                search. Drops buyers straight into `/map` with Draw
                mode auto-on so they can trace any neighbourhood. */}
            <div className="mt-5 flex justify-center">
              <Link
                to={market.withPrefix('/map') + '?draw=1'}
                data-testid="hero-search-by-map"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                           bg-white/95 hover:bg-white text-[#7042cc] text-sm font-semibold
                           shadow-lg ring-1 ring-white/60 backdrop-blur transition-transform
                           hover:scale-[1.02]"
                title="Draw any shape on the map — only homes inside show up"
              >
                <Pencil className="h-4 w-4" />
                Search by map — draw your area
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Postal lookup — useMarket() picks Eircode/Postcode/ZIP box. */}
            <div className="mt-6" data-testid={`home-postcode-lookup-${market.code.toLowerCase()}`}>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2 text-center">
                Or look up sold prices by {market.postal_unit_label}
              </p>
              <PostalSearchBox variant={isUK ? 'inline' : 'light'} country={market.name} />
            </div>
          </div>
        </div>
      </section>

      {/* Recently viewed — pick up where you left off (hidden if empty) */}
      <RecentlyViewedRail />

      {/* Saved Homes — the shortlist the visitor built earlier. Only rendered
          when the user is signed in and has at least one saved property, so
          returning visitors land straight on the homes they cared about. */}
      {isAuthenticated && savedHomes.length > 0 && (
        <HomePropertyRail
          title="Your saved homes"
          subtitle="Pick up where you left off"
          properties={savedHomes}
          viewAllHref={market.withPrefix('/dashboard?tab=saved')}
          accent="#ec4899"
          icon={Heart}
          testId="rail-saved"
          headerAction={
            <ShareShortlistButton
              propertyIds={savedHomes.map((p) => p._id || p.id).filter(Boolean)}
            />
          }
        />
      )}

      {/* Empty-state prompt — nudges signed-in visitors who haven't saved
          anything yet back into browsing so the Saved Homes rail can fill. */}
      {isAuthenticated && savedHomes.length === 0 && (
        <section
          className="pt-10 pb-2 px-4 sm:px-6 lg:px-8"
          data-testid="rail-saved-empty"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 rounded-2xl border border-pink-100 bg-gradient-to-r from-pink-50 via-white to-white p-4 sm:p-5">
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-pink-500 text-white shrink-0 shadow-sm">
                <Heart size={18} className="fill-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-pink-600 mb-0.5">
                  Save your first home
                </div>
                <p className="text-sm text-gray-700 leading-snug">
                  Tap the heart on any home you like — we&apos;ll keep them here
                  so you can flick between your favourites in one place.
                </p>
              </div>
              <Link
                to={market.withPrefix('/listings')}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold shadow-sm transition"
                data-testid="rail-saved-empty-cta"
              >
                Browse homes <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Rail 1 — Fresh on the market. The most recent inventory in the
          active market, up-front so visitors see homes before marketing. */}
      <HomePropertyRail
        title="Fresh on the market"
        subtitle={
          market.isUK ? 'Just listed across the UK' :
          market.isUS ? 'Just listed across the US' :
                        'Just listed across Ireland'
        }
        properties={rails.recent}
        viewAllHref={market.withPrefix('/listings')}
        mapHref={market.withPrefix('/map')}
        accent="#7042cc"
        icon={Home}
        testId="rail-fresh"
      />

      {/* Search by map — full-width promo card. Uses a live map tile
          preview as the background so it feels tangible; deep-links
          into `/map?draw=1` where Draw mode is already on. */}
      <section className="py-8 sm:py-10 px-4 sm:px-6 lg:px-8" data-testid="home-map-promo">
        <div className="max-w-7xl mx-auto">
          <Link
            to={market.withPrefix('/map') + '?draw=1'}
            data-testid="home-map-promo-cta"
            className="group relative flex flex-col sm:flex-row items-stretch overflow-hidden rounded-2xl ring-1 ring-gray-200 shadow-md hover:shadow-xl transition-shadow bg-white"
          >
            {/* Map tile preview */}
            <div
              className="relative sm:w-2/5 min-h-[180px] sm:min-h-[220px] bg-cover bg-center"
              style={{
                backgroundImage: `url(https://tile.openstreetmap.org/${market.isUK ? '6/32/20' : market.isUS ? '5/9/12' : '6/31/20'}.png), url(https://tile.openstreetmap.org/${market.isUK ? '6/32/21' : market.isUS ? '5/9/13' : '6/31/21'}.png)`,
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'left top, right top',
              }}
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#7042cc]/70 via-[#7042cc]/40 to-transparent" />
              {/* Fake drawn polygon overlay */}
              <svg viewBox="0 0 300 220" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
                <path d="M 60 40 Q 120 20 190 55 Q 240 80 230 140 Q 200 190 130 180 Q 60 170 45 110 Z"
                       fill="rgba(112,66,204,0.28)" stroke="#7042cc" strokeWidth="2.5" strokeDasharray="5 3" strokeLinejoin="round"/>
                <circle cx="60" cy="40" r="4" fill="#7042cc" />
                <circle cx="230" cy="140" r="4" fill="#7042cc" />
              </svg>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#7042cc] shadow-sm">
                <MapIcon className="h-3 w-3" /> Map
              </div>
            </div>
            {/* Copy */}
            <div className="flex-1 p-5 sm:p-7 flex flex-col justify-center gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7042cc]">
                New · Draw-to-search
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                Search by map — trace your ideal neighbourhood
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed max-w-xl">
                Skip the dropdowns. Draw <em>any</em> shape on the map — around a school,
                a park, a train line — and we&apos;ll only show homes inside it.
                Save the shape and get an email the moment a new home lands.
              </p>
              <div className="mt-2 inline-flex items-center gap-1.5 self-start px-4 py-2 rounded-full bg-[#7042cc] group-hover:bg-[#5b35a8] text-white text-sm font-semibold shadow-md transition-colors">
                <Pencil className="h-4 w-4" />
                Open the map
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Recent sales — market-isolated social proof powered by seeded
          listings that have been flipped to status="sold" in admin.
          Silently hides when the market has no recent sold rows. */}
      <RecentSalesRail />

      {/* Market Intelligence Strip — compact on mobile (horizontal scroll), 3-col on desktop */}
      <section className="py-8 sm:py-10 bg-gradient-to-br from-[#1a1a1a] via-[#2a1a4a] to-[#1a1a1a] text-white" data-testid="market-intel-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4 sm:mb-5">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300/90 font-semibold mb-1">
                <BarChart3 className="h-3 w-3" /> Market Intelligence
              </div>
              <h2 className="text-xl sm:text-3xl font-bold">Real data. Free. No signup.</h2>
            </div>
            <p className="hidden sm:block text-sm text-white/70 max-w-md">
              {isUK
                ? 'Built straight from HM Land Registry Price Paid Data, the MHCLG EPC register and our live UK listings. Updated continuously.'
                : 'Built straight from the Property Price Register, SEAI BER register and our live listings. Updated continuously.'}
            </p>
          </div>
          {/* Mobile: horizontal swipe; Desktop: 3-col grid */}
          <div className="flex sm:grid sm:grid-cols-3 gap-3 sm:gap-4 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible snap-x snap-mandatory scrollbar-hide">
            <Link to={market.withPrefix('/real-cost')} className="group snap-start shrink-0 sm:shrink w-[78%] sm:w-auto rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-300/40 transition-all p-4 sm:p-5 backdrop-blur-sm" data-testid="home-intel-real-cost">
              <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-amber-300/20 text-amber-300 mb-2 sm:mb-3">
                <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-white text-sm sm:text-base">My Real Cost</h3>
                <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-300 text-gray-900 px-1.5 py-0.5 rounded-sm">NEW</span>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                Stamp duty, legal fees, {isUK ? 'EPC' : 'BER'} upgrade. The all-in number — not just the asking price.
              </p>
              <div className="mt-2 sm:mt-3 text-xs font-semibold text-amber-300 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                Calculate <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
            <Link to={market.withPrefix('/area-prices')} className="group snap-start shrink-0 sm:shrink w-[78%] sm:w-auto rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#a78bfa]/40 transition-all p-4 sm:p-5 backdrop-blur-sm" data-testid="home-intel-area-prices">
              <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#7042cc]/30 text-[#a78bfa] mb-2 sm:mb-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <h3 className="font-bold text-white text-sm sm:text-base mb-1">Area Prices</h3>
              <p className="text-xs text-white/70 leading-relaxed">
                What did the neighbours actually pay? Median sale vs asking price for every {market.postal_unit_label} in {isUK ? 'the UK' : 'Ireland'}.
              </p>
              <div className="mt-2 sm:mt-3 text-xs font-semibold text-[#a78bfa] inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                Explore <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
            <Link to={market.withPrefix('/price-index')} className="group snap-start shrink-0 sm:shrink w-[78%] sm:w-auto rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-300/40 transition-all p-4 sm:p-5 backdrop-blur-sm" data-testid="home-intel-price-index">
              <div className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-300/20 text-emerald-300 mb-2 sm:mb-3">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <h3 className="font-bold text-white text-sm sm:text-base mb-1">Real Price Index</h3>
              <p className="text-xs text-white/70 leading-relaxed">
                The monthly {isUK ? 'UK' : 'Irish'} house-price index. Median sale, year-on-year, by {isUK ? 'region' : 'county'}.
              </p>
              <div className="mt-2 sm:mt-3 text-xs font-semibold text-emerald-300 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                See the index <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          </div>
        </div>
      </section>


      {/* Rails 2, 3, 4 — Great value, New homes, Rentals. Each rail is
          fed from a shared, de-duplicated pool so no property repeats. */}
      <HomePropertyRail
        title={
          market.isUK ? 'Great value under £350k' :
          market.isUS ? 'Great value under $500k' :
                        'Great value under €400k'
        }
        subtitle="Wallet-friendly homes we're loving"
        properties={rails.goodValue}
        viewAllHref={market.withPrefix(
          market.isUK ? '/listings?maxPrice=350000' :
          market.isUS ? '/listings?maxPrice=500000' :
                        '/listings?maxPrice=400000'
        )}
        accent="#10b981"
        icon={TrendingUp}
        testId="rail-good-value"
      />

      <HomePropertyRail
        title="Brand-new homes"
        subtitle="Just built · move-in ready"
        properties={rails.newHomes}
        viewAllHref={market.withPrefix('/new-homes')}
        accent="#f59e0b"
        icon={Sparkles}
        testId="rail-new-homes"
      />

      <HomePropertyRail
        title="Rentals right now"
        subtitle="Available to let this week"
        properties={rails.rentals}
        viewAllHref={market.withPrefix('/listings?type=rent')}
        accent="#3b82f6"
        icon={Bath}
        testId="rail-rentals"
      />

      {/* Featured Estate Agents — Area Sponsor carousel */}
      <FeaturedAgentsCarousel />

      {/* "What buyers can do here — that they can't do anywhere else in Ireland" */}
      <section className="relative py-12 overflow-hidden" data-testid="buyer-feature-showcase">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-[#FFF7F0] to-[#F5EFFF]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white ring-1 ring-[#7042cc]/20 text-[#5b35a8] text-[11px] font-semibold uppercase tracking-[0.16em] mb-4">
              <Sparkles className="h-3 w-3" />
              First-of-its-kind in {isUK ? 'the UK' : 'Ireland'}
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl text-gray-900 tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
            >
              Eight things you can do here<br className="hidden sm:block" />
              <span className="text-[#7042cc]"> that no other {isUK ? 'UK' : 'Irish'} portal offers.</span>
            </h2>
            <p className="text-base text-gray-600 mt-4 leading-relaxed">
              Verified data, honest copy, encrypted chat with the agent, real offers — every feature on this page is live today.
            </p>
          </div>
          <FeatureShowcaseGrid audience="buyer" countryOverride={countryOverride} />
        </div>
      </section>

      {/* Revenue conversion anchor — both sides of the platform */}
      <section className="px-4 sm:px-6 lg:px-8 -mt-4 pb-4">
        <div className="max-w-7xl mx-auto">
          <FeaturesHeroCard defaultAudience="buyer" />
        </div>
      </section>

      {/* Engagement zone — Daily Doorstep + Style Quiz entry points */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F5EFFF] via-white to-[#FFF7F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-xs font-medium text-[#7042cc] tracking-widest uppercase mb-2">
              For the dreamers
            </div>
            <h2
              className="text-3xl sm:text-4xl text-gray-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
            >
              House-hunting is more than a search bar
            </h2>
            <p className="text-gray-600 text-base mt-3 max-w-2xl mx-auto">
              Three small, friendly things you can do in under a minute — even when you&apos;re not actively looking.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            <Link
              to={market.withPrefix('/home-crush')}
              data-testid="home-crush-tile"
              className="group block rounded-2xl border-2 border-[#7042cc]/15 hover:border-[#7042cc]/40 bg-white p-6 sm:p-8 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#7042cc] to-[#5b35a8] text-white shadow-sm mb-4">
                <Heart className="h-5 w-5 fill-white" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#7042cc] font-semibold mb-1">
                Today&apos;s Doorstep
              </div>
              <h3
                className="text-xl text-gray-900 mb-2 leading-snug"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
              >
                {market.isUK
                  ? 'One beautiful British home, every morning at 8am.'
                  : market.isUS
                    ? 'One beautiful American home, every morning at 8am.'
                    : 'One beautiful Irish home, every day.'}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Hand-picked, with a short editorial story — architecture, neighbourhood, and why we love it. Bookmark-worthy daily eye candy.
              </p>
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#7042cc] group-hover:gap-1.5 transition-all">
                See today&apos;s pick
                <ArrowRight className="h-4 w-4" />
              </div>
            </Link>

            <Link
              to={market.withPrefix('/style-quiz')}
              data-testid="style-quiz-tile"
              className="group block rounded-2xl border-2 border-amber-200/60 hover:border-amber-300 bg-white p-6 sm:p-8 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-sm mb-4">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
                ~60-second quiz
              </div>
              <h3
                className="text-xl text-gray-900 mb-2 leading-snug"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
              >
                What&apos;s your home style?
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Sea-air or city café? Velvet snug or whitewashed walls? Six quick questions, and we&apos;ll match you with {isUK ? 'UK' : 'Irish'} homes that feel like you.
              </p>
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 group-hover:gap-1.5 transition-all">
                Take the quiz
                <ArrowRight className="h-4 w-4" />
              </div>
            </Link>

            <Link
              to={market.withPrefix('/design-my-room')}
              data-testid="design-room-tile"
              className="group block rounded-2xl border-2 border-fuchsia-200/60 hover:border-fuchsia-300 bg-white p-6 sm:p-8 transition-all hover:shadow-lg relative overflow-hidden"
            >
              <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-[#7042cc] text-white text-[9px] font-bold tracking-wider">
                NEW
              </span>
              <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-[#7042cc] text-white shadow-sm mb-4">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-semibold mb-1">
                3 free renders / day
              </div>
              <h3
                className="text-xl text-gray-900 mb-2 leading-snug"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
              >
                Re-design your room with AI.
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Snap a phone photo, pick a style — {isUK ? 'Victorian, Modern British, Coastal' : 'Scandi, Georgian, Coastal Irish'} or three more — and watch the same room transform in under a minute.
              </p>
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-fuchsia-700 group-hover:gap-1.5 transition-all">
                Design my room
                <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Browse by Area — internal SEO links */}
      {areas.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-xs font-medium text-[#7042cc] tracking-widest uppercase mb-2">
                Explore {isUK ? 'the UK' : 'Ireland'}
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold text-gray-800">
                Browse properties by area
              </h2>
              <p className="text-gray-600 text-base mt-3 max-w-2xl mx-auto">
                Curated landing pages for every county and neighbourhood we cover. Each one includes a verified listing count, price range and AI investment overview.
              </p>
            </div>
            <div className="space-y-6" data-testid="home-areas-section">
              {areas.map((c) => (
                <div key={c.county_slug} className="border-b border-gray-100 pb-5 last:border-b-0">
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <Link
                      to={`/buy/${c.county_slug}`}
                      data-testid={`home-county-${c.county_slug}`}
                      className="text-lg font-semibold text-gray-900 hover:text-[#7042cc]"
                    >
                      {c.county}
                    </Link>
                    <span className="text-xs text-gray-500">
                      {c.total} {c.total === 1 ? 'listing' : 'listings'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.areas.slice(0, 8).map((a) => (
                      <Link
                        key={a.slug || a.area}
                        to={`/buy/${c.county_slug}/${a.slug || a.area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        data-testid={`home-area-${a.slug || a.area}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5EFFF] border border-[#7042cc]/20 text-[#7042cc] text-sm hover:bg-[#7042cc]/10"
                      >
                        {a.area}
                        <span className="text-[10px] text-[#7042cc]">{a.count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section — warm */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F5EFFF] via-[#FFF7F0] to-[#FFEFE3]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl text-gray-900 tracking-tight mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
          >
            Find a place you&apos;ll love coming back to.
          </h2>
          <p className="text-base text-gray-700 mb-7 max-w-2xl mx-auto">
            Browse honest listings, run the numbers, and chat with an AI that&apos;s actually on your side.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup">
              <Button className="bg-[#7042cc] hover:bg-[#5b35a8] text-white px-8 py-6 rounded-xl text-base font-medium shadow-lg shadow-[#7042cc]/20">
                Start your search
              </Button>
            </Link>
            <Link to={market.withPrefix('/listings')}>
              <Button variant="outline" className="border-[#7042cc]/20 text-[#7042cc] hover:bg-[#7042cc]/5 px-8 py-6 rounded-xl text-base font-medium">
                Browse homes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* === Selling? — split CTA: private seller vs estate agent === */}
      {/* Agent Match hero card — bold-and-different from the market intel grid.
          Sits between the featured listings and the "Two ways to list" section
          so both buyers-turned-sellers and existing seller-intent visitors see it. */}
      <section className="relative overflow-hidden py-12 bg-[#0b0d17] text-white" data-testid="home-agent-match-hero">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #f59e0b 0%, transparent 45%), radial-gradient(circle at 85% 80%, #7042cc 0%, transparent 55%)' }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-3">
              <Gavel className="h-3 w-3" /> Brand-new · Agent Match
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold leading-tight">
              Selling? Get <span className="text-amber-300">verified agents</span> pitching to you.
            </h2>
            <p className="text-white/70 mt-4 max-w-lg text-base sm:text-lg">
              Post an anonymous 3-minute brief. Within 48 hours, 3–5 {isUK ? 'Propertymark' : 'PSRA'}-registered
              agents in your area submit their proposals — asking price, commission %, marketing plan.
              You pick the winner. No cold calls until you do.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={market.withPrefix('/agent-match/sell')} data-testid="home-agent-match-cta-sell">
                <Button size="lg" className="bg-amber-300 hover:bg-amber-200 text-[#0b0d17] font-semibold">
                  Get pitches for my home <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to={market.withPrefix('/agent-match')} data-testid="home-agent-match-cta-learn">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  How it works
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60">
              <span>· Anonymous by default</span>
              <span>· 48h response window</span>
              <span>· Zero seller fees</span>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-5 shadow-2xl">
            <div className="flex items-center justify-between text-xs text-white/60 mb-4">
              <span className="uppercase tracking-wider">Sample proposal</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">2 of 4</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-[#0b0d17] font-bold text-sm">
                {isUK ? 'FT' : 'SF'}
              </div>
              <div>
                <div className="font-semibold text-white">{isUK ? 'Foxtons' : 'Sherry FitzGerald'}</div>
                <div className="text-xs text-white/60">
                  {isUK ? 'Propertymark' : 'PSRA'} · 12 sales in your area this year
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wide text-white/50">Proposed asking</div>
                <div className="text-lg font-bold text-amber-300">{isUK ? '£385,000' : '€435,000'}</div>
              </div>
              <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wide text-white/50">Commission</div>
                <div className="text-lg font-bold">1.15%</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 border-t border-gray-100" data-testid="home-selling-split">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-[11px] font-semibold uppercase tracking-wider">
              Selling?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3">Two ways to list on lovinahome</h2>
            <p className="text-base text-gray-600 mt-3 max-w-xl mx-auto">
              Whether you&apos;re selling your own home or running an agency — there&apos;s a track built for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {/* Private seller card */}
            <Link
              to={market.withPrefix('/sell-my-home')}
              data-testid="home-cta-private"
              className="group block bg-gradient-to-br from-amber-50 via-white to-white rounded-2xl p-7 border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-md">
                  <Home className="h-6 w-6" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">For home owners</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Sell my home</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                Skip the agent and the commission. Our AI writes your listing, suggests a price,
                and your enquiries come straight to you.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-600 mt-0.5" />{fsboLine}</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-600 mt-0.5" />AI writes everything — keep the words you like</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-600 mt-0.5" />Buyer enquiries to your inbox</li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-1.5 text-amber-700 font-semibold group-hover:gap-2.5 transition-all">
                Start your listing <ArrowRight className="h-4 w-4" />
              </div>
            </Link>

            {/* Agent card */}
            <Link
              to={market.withPrefix('/list-with-us')}
              data-testid="home-cta-agent"
              className="group block bg-gradient-to-br from-[#F5EFFF] via-white to-white rounded-2xl p-7 border-2 border-[#7042cc]/20 hover:border-[#7042cc] hover:shadow-xl transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#7042cc] text-white flex items-center justify-center shadow-md">
                  <Award className="h-6 w-6" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#5b35a8]">For estate agents</div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Agent area</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {isUK ? 'Propertymark' : 'PSRA'}-verified, branded profile, bulk listing tools and direct buyer enquiries.
                First 30 days free.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#7042cc] mt-0.5" />Real &ldquo;Verified Agent&rdquo; badge ({isUK ? 'Propertymark' : 'PSRA'}-checked)</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#7042cc] mt-0.5" />AI Listing Assistant on every property</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#7042cc] mt-0.5" />Free 30 days, cancel anytime</li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[#7042cc] font-semibold group-hover:gap-2.5 transition-all">
                Start your free trial <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePageProfessional;

/**
 * WarmCard — uniform card for the "Everything you need" section.
 * Soft tinted background per tone, brand-purple accent type, rounded-2xl,
 * no fake data screenshots. Pass `wide` to span 2 columns.
 */
const WarmCard = ({ tone, title, description, icon, accent, wide }) => {
  const tones = {
    purple: { bg: 'bg-[#F5EFFF]', icon: 'bg-[#7042cc] text-white', accent: 'text-[#7042cc]' },
    emerald: { bg: 'bg-[#EAF7F0]', icon: 'bg-emerald-500 text-white', accent: 'text-emerald-600' },
    amber: { bg: 'bg-[#FFF3E0]', icon: 'bg-amber-500 text-white', accent: 'text-amber-700' },
    rose: { bg: 'bg-[#FFECEC]', icon: 'bg-rose-500 text-white', accent: 'text-rose-600' },
    cyan: { bg: 'bg-[#E0F4F7]', icon: 'bg-cyan-600 text-white', accent: 'text-cyan-700' },
    indigo: { bg: 'bg-[#EDEEFE]', icon: 'bg-indigo-500 text-white', accent: 'text-indigo-700' },
  }[tone] || { bg: 'bg-gray-50', icon: 'bg-gray-700 text-white', accent: 'text-gray-700' };
  return (
    <div
      className={`${tones.bg} rounded-2xl p-6 sm:p-7 transition-transform hover:-translate-y-0.5 hover:shadow-md ${
        wide ? 'sm:col-span-2 lg:col-span-3' : ''
      }`}
    >
      <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${tones.icon} shadow-sm mb-4`}>
        {icon}
      </div>
      <h3
        className="text-xl text-gray-900 mb-2 leading-snug"
        style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
      >
        {title}
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-3">{description}</p>
      <div className={`text-xs font-medium ${tones.accent}`}>{accent}</div>
    </div>
  );
};
